import type { ComponentProps, KeyboardEvent, ReactNode } from "react"
import { useEffect, useRef } from "react"
import { IconCheckOutline18 as Check } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"
import { ContextMenuItem, ContextMenuSubContent } from "@productlane/ui"

const ROW_SELECTOR = '[role^="menuitem"]:not([data-disabled])'

function isTypingKey(event: KeyboardEvent) {
	if (event.metaKey || event.ctrlKey || event.altKey) return false
	return event.key.length === 1 || event.key === "Backspace"
}

interface SearchSubContentProps {
	placeholder: string
	query: string
	onQueryChange: (query: string) => void
	emptyLabel: string
	isEmpty: boolean
	children: ReactNode
	className?: string
}

/**
 * A submenu with a search field on top, like Linear's label picker. Typing anywhere in
 * the submenu goes to the field; arrow down walks into the rows and Enter picks the first.
 */
export function SearchSubContent({
	placeholder,
	query,
	onQueryChange,
	emptyLabel,
	isEmpty,
	children,
	className,
}: SearchSubContentProps) {
	const inputRef = useRef<HTMLInputElement>(null)
	const listRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		// Radix focuses the submenu itself on open; take it over one frame later.
		const frame = requestAnimationFrame(() =>
			inputRef.current?.focus({ preventScroll: true }),
		)
		return () => cancelAnimationFrame(frame)
	}, [])

	const firstRow = () =>
		listRef.current?.querySelector<HTMLElement>(ROW_SELECTOR) ?? null

	return (
		<ContextMenuSubContent
			className={cn("w-64 p-0", className)}
			sideOffset={4}
			alignOffset={-5}
			onKeyDownCapture={(event) => {
				const input = inputRef.current
				if (!input || event.target === input || !isTypingKey(event)) return
				// Moving focus before the keystroke lands sends the character into the field,
				// and stopping it here keeps Radix's typeahead from jumping between rows.
				input.focus({ preventScroll: true })
				event.stopPropagation()
			}}
		>
			<div className="border-b border-gray-200 px-3 py-2 dark:border-gray-700/80">
				<input
					ref={inputRef}
					value={query}
					onChange={(event) => onQueryChange(event.target.value)}
					placeholder={placeholder}
					aria-label={placeholder}
					className="w-full border-none bg-transparent p-0 text-sm text-primary outline-none placeholder:text-tertiary focus:ring-0"
					onKeyDown={(event) => {
						if (event.key === "ArrowDown") {
							event.preventDefault()
							event.stopPropagation()
							firstRow()?.focus()
							return
						}
						if (event.key === "Enter") {
							event.preventDefault()
							event.stopPropagation()
							firstRow()?.click()
							return
						}
						// Escape and Tab close the menu; arrow left closes the submenu once the field is empty.
						if (event.key === "Escape" || event.key === "Tab") return
						if (event.key === "ArrowLeft" && !query) return
						event.stopPropagation()
					}}
				/>
			</div>
			<div ref={listRef} className="max-h-80 overflow-y-auto p-1 pb-2">
				{children}
				{isEmpty && (
					<div className="px-2 py-1.5 text-sm text-tertiary">{emptyLabel}</div>
				)}
			</div>
		</ContextMenuSubContent>
	)
}

type OptionProps = Omit<ComponentProps<typeof ContextMenuItem>, "children"> & {
	label: ReactNode
	detail?: ReactNode
	leading?: ReactNode
	selected?: boolean
}

/** One pickable row: a leading mark, the label, a muted detail, and a check that keeps its slot. */
export function MenuOption({
	label,
	detail,
	leading,
	selected = false,
	className,
	...props
}: OptionProps) {
	return (
		<ContextMenuItem className={cn("gap-2", className)} {...props}>
			{leading ? (
				<span className="flex h-4 w-4 flex-none items-center justify-center">
					{leading}
				</span>
			) : null}
			<span className="min-w-0 flex-1 truncate">
				{label}
				{detail ? (
					<span className="ml-1.5 font-normal text-tertiary">{detail}</span>
				) : null}
			</span>
			<Check
				aria-hidden
				className={cn(
					"h-4 w-4 flex-none text-secondary",
					selected ? "opacity-100" : "opacity-0",
				)}
			/>
		</ContextMenuItem>
	)
}
